import type { CSSObject } from '@mui/material/styles';
import { intent, radius, sizes, spacingTokens, state, surface, text, typography } from '../../theme/tokens/index.ts';

/**
 * Shared style recipe for the SelectionChip pill (Figma "Check Token", node 1537:5960).
 *
 * The chip renders through TWO different roots and they must look identical:
 * - **plain mode** — a MUI `ToggleButton`, styled from the theme (`MuiToggleButton` →
 *   `&.SelectionChip-root` in theme/components.ts);
 * - **menu mode** — a styled `div` in SelectionChip.tsx, because a `<button>` can't nest the two
 *   sibling controls that mode needs, which puts it out of the ToggleButton theme's reach.
 *
 * Everything both roots share lives here so a token change lands in one place. The parts that
 * genuinely differ stay at the call sites: the size CLASS (`.MuiToggleButton-sizeSmall` vs
 * `.SelectionChip-sizeSmall`), the horizontal padding (the menu pill's controls sit flush), and
 * how each root expresses its states (MUI's `.Mui-selected` / `.Mui-disabled` classes vs the
 * styled component's props).
 *
 * Importing component styles into the theme is the established pattern here — components.ts
 * already pulls in RadioIcons, AvatarFallback, PickerField and StepperIcon. This module imports
 * tokens only, so there is no cycle.
 */

/** The pill itself: shape, resting fill, border and label colour. */
export const chipShell: CSSObject = {
  borderRadius: radius.pill,
  borderStyle: 'solid',
  borderWidth: sizes.border.bd1,
  // Children sit flush; inter-element spacing comes from the checkbox's own hit-box inset and
  // the label's padding (the Figma "text container"), matching node 1537:5960.
  gap: 0,
  backgroundColor: surface.default,
  borderColor: intent.neutral.border,
  color: text.primary,
};

/** Height + label typography per size. Padding differs per root, so it is NOT included. */
export const chipSize: Record<'small' | 'medium', CSSObject> = {
  small: { height: sizes.height.h28, ...typography.body2 },
  medium: { height: sizes.height.h36, ...typography.body1 },
};

/** Selected: subtle purple fill, primary border, semibold primary label. */
export const chipSelected: CSSObject = {
  backgroundColor: intent.primary.subtle,
  borderColor: intent.primary.border,
  color: intent.primary.solid,
  fontWeight: typography.body1Bold.fontWeight,
};

/** Disabled + unselected: flat grey fill and a same-hue border. */
export const chipDisabled: CSSObject = {
  backgroundColor: intent.neutral.disabled,
  borderColor: intent.neutral.disabled,
  color: text.disabled,
};

/** Disabled + selected: keeps the subtle purple fill, drops the border. */
export const chipDisabledSelected: CSSObject = {
  backgroundColor: intent.primary.subtle,
  borderColor: 'transparent',
  color: text.disabled,
};

/** Leading icon slot (Figma "showIcon"). Its colour follows the label in each state. */
export const chipIconSlot: CSSObject = {
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
};

/** Leading icon glyph size per chip size: 18px small / 20px medium. */
export const chipIconSvgSize: Record<'small' | 'medium', CSSObject> = {
  small: { width: sizes.height.h18, height: sizes.height.h18 },
  medium: { width: sizes.height.h20, height: sizes.height.h20 },
};

/** The trailing spinner's slot (`loading`). Layout only — see the loader note in DECISIONS. */
export const chipLoaderSlot: CSSObject = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

/**
 * The round checkbox indicator, mirroring the Figma Checkbox sub-component: an 18px circle,
 * white with a hairline ring at rest, filled primary once anything is selected.
 *
 * Keep these MERGED into a single `.SelectionChip-circle` rule at each call site — a second
 * rule with the same key in one object literal REPLACES the first rather than extending it,
 * which once turned the circle square.
 */
export const chipCircle: CSSObject = {
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: sizes.height.h18,
  height: sizes.height.h18,
  borderRadius: radius.pill,
  backgroundColor: surface.white,
  boxShadow: `inset 0 0 0 ${sizes.border.bd1}px ${intent.neutral.border}`,
};

export const chipCircleSelected: CSSObject = {
  backgroundColor: intent.primary.solid,
  boxShadow: 'none',
};

export const chipCircleDisabled: CSSObject = {
  backgroundColor: intent.neutral.disabled,
  boxShadow: `inset 0 0 0 ${sizes.border.bd1}px ${state.disabled.border}`,
};

export const chipCircleDisabledSelected: CSSObject = {
  backgroundColor: text.disabled,
  boxShadow: 'none',
};

/** The check / minus glyph inside the circle. */
export const chipGlyph: CSSObject = {
  width: sizes.height.h12,
  height: sizes.height.h12,
  color: text.white,
};

/** The 28px hit box that centres the circle. */
export const chipIndicatorBox: CSSObject = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: sizes.height.h28,
  height: sizes.height.h28,
};

/** The label's inset inside the pill (Figma "text container"). */
export const CHIP_LABEL_INSET = spacingTokens.sp4;
